import axios from "axios";
import { reformatDateForServer } from "@/helpers/workWithDates";
import { FINE_GROUP_ALT, BONUS_GROUP_ALT } from "@/constants/fineAndBonusGroups";
import { userPermissionsList } from "@/constants/userPermissions";
import { wordForm } from "@/utils/text/wordForm";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';

const apiUrl = "index.php";

const WITHOUT_GROUP_BONUS_ITEMS = -1;
const WITHOUT_GROUP_FINE_ITEMS = -2;

const columnsOrder = ["id", "fio", "post", "filial", "date", "reason", "author", "sum", "deadline", "comment"];

const selectColors = {
    item: {
        [BONUS_GROUP_ALT]: "#D0F4E3",
        [FINE_GROUP_ALT]: "#FFE6F2",
    },
    group: {
        [BONUS_GROUP_ALT]: "#12C971",
        [FINE_GROUP_ALT]: "#FF238D",
    },
};

const tableSortingValues = {
    fio: "worker_name",
    date: "date",
    author: "author_name",
    deadline: "deadline",
    sum: "amount",
    reason: "type_name",
    filial: "filial_name",
    post: "first_worker_post",
    id: "id",
    transaction_id: "transaction_id",
    comment: "comment"
};

const columnStringFiltersValues = {
    id: {
        1: "id",
        2: "id_like",
    },
    transaction_id: {
        1: "transaction_id",
        2: "transaction_id_like",
    },
    fio: {
        1: "worker_name",
        2: "worker_like",
    },
    reason: {
        1: "bonus_fine",
        2: "bonus_fine_like",
    },
    author: {
        1: "author_name",
        2: "author_like",
    },
    comment: {
        1: "comment",
        2: "comment_like",
    },
};

const columnNumberFilterValues = {
    1: (value) => `<${value}`,
    2: (value) => `>${value}`,
    3: (value) => value,
    4: ({ start, end }) => `${start}-${end}`,
};

const columnDateFilterValues = {
    date: {
        date: (date) => `start=${date}&finish=${date}`,
        before: (date) => `finish=${date}`,
        after: (date) => `start=${date}`,
        range: (date) => {
            const [start, end] = date.split(",");
            return `start=${reformatDateForServer(start)}&finish=${reformatDateForServer(end)}`;
        },
    },
    deadline: {
        date: (date) => `deadline_start=${date}&deadline_finish=${date}`,
        before: (date) => `deadline_finish=${date}`,
        after: (date) => `deadline_start=${date}`,
        range: (date) => {
            const [start, end] = date.split(",");
            return `deadline_start=${reformatDateForServer(start)}&deadline_finish=${reformatDateForServer(end)}`;
        },
    },
};

const columnListFilterValues = {
    post: {
        1: (values) => {
            const posts = [];
            values.forEach((position) => {
                posts.push(`post_code[]=${position}`);
            });
            return posts.join("&");
        },
        2: (values) => {
            const posts = ["exclPosts=1"];
            values.forEach((position) => {
                posts.push(`post_code[]=${position}`);
            });
            return posts.join("&");
        },
    },
    filial: {
        1: (values) => {
            const filials = [];
            values.forEach((filial) => {
                filials.push(`filial_id[]=${filial}`);
            });
            return filials.join("&");
        },
        2: (values) => {
            const filials = ["exclFilial=1"];
            values.forEach((filial) => {
                filials.push(`filial_id[]=${filial}`);
            });
            return filials.join("&");
        },
    },
};

const getColumnQueryParams = (columnFilters) => {
    const queriedFilters = [];

    const entries = Object.entries(columnFilters);
    entries.forEach(([filterKey, filterValue]) => {
        if (filterValue.type === "string") {
            const query = columnStringFiltersValues[filterKey][filterValue.searchType];
            queriedFilters.push(`${query}=${filterValue.value}`);
        }
        if (filterValue.type === "number") {
            let key = filterKey;
            if (key === "sum") {
                key = "amount";
            }
            queriedFilters.push(`${key}=${columnNumberFilterValues[filterValue.searchType](filterValue.value)}`);
        }
        if (filterValue.type === "date" && filterValue.value) {
            queriedFilters.push(columnDateFilterValues[filterKey][filterValue.searchType](filterValue.value));
        }
        if (filterValue.type === "list") {
            queriedFilters.push(
                columnListFilterValues[filterKey][filterValue.searchType](filterValue.value.split(",")),
            );
        }
    });

    return queriedFilters;
};

const getQueryParams = (filters) => {
    let queryParams = [`page=${filters.page}`];

    const {
        id,
        transaction_id,
        filials,
        positions,
        workers,
        fineAndBonusReasons,
        explanationExistence,
        filialsExcept,
        positionsExcept,
        finesAndBonusesReasonsExcept,
        sortBy,
        sortType,
        startDate,
        endDate,
        columnFilters,
    } = filters;

    if (id) {
        queryParams.push(`id[]=${id}`);
    }

    if (transaction_id) {
        queryParams.push(`transaction_id[]=${transaction_id}`);
    }

    if (filials && filials.length) {
        filials.forEach((filial) => {
            queryParams.push(`filial_id[]=${filial.value}`);
        });
        if (filialsExcept) {
            queryParams.push("exclFilial=1");
        }
    }
    if (positions && positions.length) {
        positions.forEach((position) => {
            queryParams.push(`post_code[]=${position.value}`);
        });
        if (positionsExcept) {
            queryParams.push("exclPosts=1");
        }
    }
    if (workers && workers.length) {
        workers.forEach((worker) => {
            queryParams.push(`author_id[]=${worker.value}`);
        });
    }
    if (fineAndBonusReasons && fineAndBonusReasons.length) {
        fineAndBonusReasons.forEach((reason) => {
            queryParams.push(`bonus_or_fine_id[]=${reason.value}`);
        });
        if (finesAndBonusesReasonsExcept) {
            queryParams.push("exclBonusFine=1");
        }
    }
    if (sortBy && sortType) {
        queryParams.push(`sort=${sortType === "desc" ? "-" : ""}${tableSortingValues[sortBy]}`);
    }
    if (startDate && endDate) {
        queryParams.push(`start=${startDate}&finish=${endDate}`);
    }
    if (explanationExistence) {
        queryParams.push(`explanatory_type=${explanationExistence.value}`);
    }

    if (columnFilters && Object.keys(columnFilters).length) {
        const columnQueryParams = getColumnQueryParams(filters.columnFilters);
        queryParams = queryParams.concat(columnQueryParams);
    }

    return queryParams;
};

const getFormDataForFineOrBonus = (fineOrBonusData) => {
    const formData = new FormData();
    formData.append("type", fineOrBonusData.createFineOrBonus === "fine" ? "1" : "0");
    formData.append("type_id", fineOrBonusData.fineOrBonus.value);
    formData.append("amount", fineOrBonusData.amount);
    formData.append("date", reformatDateForServer(fineOrBonusData.date));
    formData.append("filial_id", fineOrBonusData.filial.value);

    if (!Array.isArray(fineOrBonusData.worker)) {
        fineOrBonusData.worker = [fineOrBonusData.worker];
    }
    fineOrBonusData.worker.forEach((w)=>{
        formData.append("worker_id[]", w.value);
    });

    formData.append("company_id", fineOrBonusData.company.value);

    formData.append("explanatory_required", fineOrBonusData.isExplanatoryRequired ? "1" : "0");
    if (fineOrBonusData.addedBy) {
        formData.append("author_id", fineOrBonusData.addedBy.value);
    }
    if (fineOrBonusData.position) {
        formData.append("post_code", fineOrBonusData.position.value);
    }
    if (fineOrBonusData.deadline && fineOrBonusData.isExplanatoryRequired) {
        formData.append("deadline", reformatDateForServer(fineOrBonusData.deadline));
    }
    if (fineOrBonusData.comment) {
        formData.append("comment", fineOrBonusData.comment);
    }

    return formData;
};

const getFormDataForUpdateFineOrBonus = (fineOrBonusData) => {
    const formData = new FormData();

    // formData.append("type", fineOrBonusData.fineOrBonus.type);
    formData.append("type", fineOrBonusData.createFineOrBonus === "fine" ? "1" : "0");
    formData.append("type_id", fineOrBonusData.fineOrBonus.value);
    formData.append("amount", fineOrBonusData.amount);
    if (fineOrBonusData.comment) {
        formData.append("comment", fineOrBonusData.comment);
    }

    return formData;
};

const state = () => ({
    collections: {
        filials: [],
        companies: [],
        positions: [],
        workers: [],
        finesAndBonuses: {
            groups: [],
            items: [],
            deletedItems: [],
        },
    },
    records: [],
    columns: [],
    userColumns: [],
    isMoreDataAvailable: true,
    totalCount: 0,
    queryForGoogleSheets: [],
    currentUserWorker: "",
});

const getters = {
    isAllowedToEdit(_, __, rootState) {
        return (
            rootState.userPermissions.includes(userPermissionsList.referenceBooks.finesAndBonuses.edit) ||
            rootState.userPermissions.includes(userPermissionsList.referenceBooks.finesAndBonuses.editAll)
        );
    },
    finesCollection: (state) => ({
        groups: state.collections.finesAndBonuses.groups.filter(
            (g) => g.type === FINE_GROUP_ALT || g.type === WITHOUT_GROUP_FINE_ITEMS,
        ),
        items: state.collections.finesAndBonuses.items.filter(
            (i) => i.group.split("-")[0] === FINE_GROUP_ALT.toString() || i.group === WITHOUT_GROUP_FINE_ITEMS,
        ),
        deletedItems: state.collections.finesAndBonuses.deletedItems.filter(
            (i) => i.group.split("-")[0] === FINE_GROUP_ALT.toString() || i.group === WITHOUT_GROUP_FINE_ITEMS,
        ),
    }),
    bonusesCollection: (state) => ({
        groups: state.collections.finesAndBonuses.groups.filter(
            (g) => g.type === BONUS_GROUP_ALT || g.type === WITHOUT_GROUP_BONUS_ITEMS,
        ),
        items: state.collections.finesAndBonuses.items.filter(
            (i) => i.group.split("-")[0] === BONUS_GROUP_ALT.toString() || i.group === WITHOUT_GROUP_BONUS_ITEMS,
        ),
        deletedItems: state.collections.finesAndBonuses.deletedItems.filter(
            (i) => i.group.split("-")[0] === BONUS_GROUP_ALT.toString() || i.group === WITHOUT_GROUP_BONUS_ITEMS,
        ),
    }),
};

const actions = {
    async fetchWorkerRecords({ rootState, commit, state }, filters) {
        const token = rootState.authToken;
        const queryParams = getQueryParams(filters);

        commit("setQueryForGoogleSheets", queryParams);
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/index&per-page=30&${queryParams.join("&")}`, token);

        const { data, headers } = await axios.get(url);
        const totalItemsCount = +headers["x-pagination-total-count"];

        commit("setTotalCount", totalItemsCount);

        if (filters.page === 1) {
            commit("setFirstRecords", data);
        } else {
            commit("setRecords", data);
        }

        if (totalItemsCount > state.records.length) {
            commit("setIsMoreDataAvailable", true);
        } else {
            commit("setIsMoreDataAvailable", false);
        }
    },
    async fetchFilials({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/filial/index`, token);

        const { data } = await axios.get(url);

        const filials = data.items.map((filial) => ({ label: filial.filial_name, value: filial.id }));

        commit("setFilialsCollection", filials);
    },
    async fetchPositions({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/position`, token);

        const { data } = await axios.get(url);
        const positions = Object.entries(data).map(([positionCode, { name }]) => ({
            label: name,
            value: positionCode,
        }));

        commit("setPositionsCollection", positions);
    },
    async fetchWorkers({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker/list-with-photo&sort=fio`, token);

        const { data } = await axios.get(url);
        const workers = data.map((worker) => ({
            label: worker.fio,
            value: worker.id,
            filial_id: worker.filial_id ? worker.filial_id : null,
            filial_name: worker.filial_name ? worker.filial_name : null,
            filial_company_id: worker.filial_company_id ? worker.filial_company_id : null,
            filial_company_name: worker.filial_company_name ? worker.filial_company_name : null,
            company_id: worker.company_id ? worker.company_id : null,
            company_name: worker.company_name ? worker.company_name : null,
            posts: worker.posts.map(post => ({
                value: post.code,
                label: post.name
            })),
            post_names: worker.post_names,
            photo: worker.photo,
            formatLabel: `
                <div style="display: flex; align-items: center;">
                    <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
                        <img src="${worker.photo ? worker.photo : '/redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
                    </div>
                    <div>
                        <div style="">${worker.fio}</div>
                        <div style="color: #8E9CBB; font-size: 13px;">${worker.filial_name}, ${worker.post_names}</div>   
                    </div>
                </div>
            `,
        }));

        commit("setWorkersCollection", workers);
    },

    async fetchCurrentUserWorker({ commit, rootState }) {
        const token = rootState.authToken;

        await axios.get(addAuthKey(`${apiUrl}/?r=api/worker-profile/current-user-worker`, token))
            .then(response => { 
                commit("setCurrentUserWorker", response.data);
            });
    },

    async fetchCompanies({ commit, rootState }) {
        const token = rootState.authToken;
    
        const { data } = await axios.get(addAuthKey(`${apiUrl}/?r=api/directory/company`, token));

        let companies = [];

        Object.entries(data).forEach(([key, value]) => {
            companies.push({
                value: key,
                label: value
            })
        });
        
        commit("setCompanies", companies);
    
        return data;
    },

    async fetchFinesAndBonuses({ rootState, commit }, {post, personal_history_id = null}) {
        const token = rootState.authToken;

        const fineBonusFetchedGroups = await axios.get(addAuthKey(`${apiUrl}?r=api/bonus-fine-category/index&status_type=active`, token));
        const fineItems = await axios.get(addAuthKey(`${apiUrl}?r=api/fine/index&post_codes=${post ? post : ""}&status_type=active&personal_history_id=${personal_history_id ? personal_history_id : ""}`, token));
        const bonusItems = await axios.get(addAuthKey(`${apiUrl}?r=api/bonus/index&post_codes=${post ? post : ""}&status_type=active&personal_history_id=${personal_history_id ? personal_history_id : ""}`, token));

        const bonusGroups = [];
        const fineGroups = [];

        fineBonusFetchedGroups.data.forEach((group) => {
            const groupObject = {
                label: group.name,
                name: `${group.type}-${group.id}`,
                type: group.type,
                color: selectColors.group[group.type],
                sort: group.sort,
            };
            if (group.type === BONUS_GROUP_ALT) {
                bonusGroups.push(groupObject);
            }
            if (group.type === FINE_GROUP_ALT) {
                fineGroups.push(groupObject);
            }
        });

        const groups = [
            ...bonusGroups.sort((a, b) => b.sort - a.sort),
            {
                label: "Премии без категории",
                name: `${BONUS_GROUP_ALT}-${WITHOUT_GROUP_BONUS_ITEMS}`,
                type: WITHOUT_GROUP_BONUS_ITEMS,
                color: selectColors.group[BONUS_GROUP_ALT],
            },
            ...fineGroups.sort((a, b) => b.sort - a.sort),
            {
                label: "Штрафы без категории",
                name: `${FINE_GROUP_ALT}-${WITHOUT_GROUP_FINE_ITEMS}`,
                type: WITHOUT_GROUP_FINE_ITEMS,
                color: selectColors.group[FINE_GROUP_ALT],
            },
        ];

        const items = [];
        const deletedItems = [];

        bonusItems.data.forEach((item) => {
            const { text, bonusFiles, link } = item;
            const filesCount = bonusFiles && bonusFiles.length;

            let subLabel;

            if (text) subLabel = text;
            else if (filesCount) {
                subLabel = `${filesCount} ${wordForm(filesCount, ["файл", "файла", "файлов"])} ${
                    link && "и ссылка в описании"
                }`;
            } else if (link) {
                subLabel = "Ссылка в описании";
            }

            const itemObject = {
                label: item.description,
                value: item.id,
                group: `${BONUS_GROUP_ALT}-${item.category_id}`,
                amount: item.amount_bonus,
                type: BONUS_GROUP_ALT,
                subLabel,
            };
            if (!item.category_id) {
                itemObject.group = `${BONUS_GROUP_ALT}-${WITHOUT_GROUP_BONUS_ITEMS}`;
            }

            items.push({ ...itemObject, backgroundColor: selectColors.item[BONUS_GROUP_ALT] });
        });
        fineItems.data.forEach((item) => {
            const { text, fineFiles, link } = item;
            const filesCount = fineFiles && fineFiles.length;

            let subLabel;

            if (text) subLabel = text;
            else if (filesCount) {
                subLabel = `${filesCount} ${wordForm(filesCount, ["файл", "файла", "файлов"])} ${
                    link && "и ссылка в описании"
                }`;
            } else if (link) {
                subLabel = "Ссылка в описании";
            }

            const itemObject = {
                label: item.description,
                value: item.id,
                group: `${FINE_GROUP_ALT}-${item.category_id}`,
                amount: item.amount_fine,
                type: FINE_GROUP_ALT,
                subLabel,
            };
            if (!item.category_id) {
                itemObject.group = `${FINE_GROUP_ALT}-${WITHOUT_GROUP_FINE_ITEMS}`;
            }

            items.push({ ...itemObject, backgroundColor: selectColors.item[FINE_GROUP_ALT] });
        });

        commit("setFinesAndBonuses", { groups, items, deletedItems });
    },
    async fetchColumns({ rootState, commit }, userId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);
        const columns = data
            .map((column) => ({
                id: column.code,
                label: column.short_name,
                fullName: column.name,
                type: column.data_type,
                level: column.level,
                isCategory: column.is_category,
                disabled: column.disabled,
            }))
            .sort((a, b) => columnsOrder.indexOf(a.id) - columnsOrder.indexOf(b.id));

        commit("setColumns", columns);
    },
    async fetchUserColumns({ rootState, commit }) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/user-columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);

        commit("setUserColumns", data.columns);
    },
    async createFineOrBonus({ rootState, commit }, fineOrBonusForm) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/create`, token);
        const formData = getFormDataForFineOrBonus(fineOrBonusForm);

        const { data } = await axios.post(url, formData);

        data.forEach((d) => {
            if (d.success) {
                commit("putNewFineOrBonus", d.model);
            }

        });
        return data;
    },
    async updateFineOrBonus({ rootState, commit }, fineOrBonusData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/update&id=${fineOrBonusData.id}`, token);
        const formData = getFormDataForUpdateFineOrBonus(fineOrBonusData);

        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("updateFineOrBonus", data.model);
        }

        return data;
    },
    async saveUserColumns({ rootState, commit }, userColumns) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/save-user-columns`, token);
        const formData = new FormData();

        formData.append("user_id", userId);
        formData.append("columns", JSON.stringify(userColumns));

        await axios.post(url, formData);

        commit("setUserColumns", userColumns);
    },
    async deleteFineOrBonus({ rootState, commit }, fineOrBonusId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/delete&id=${fineOrBonusId}`, token);

        const { data } = await axios.get(url);

        if (data.success) {
            commit("deleteFineOrBonus", fineOrBonusId);
        }

        return data;
    },
    async addExplanatory({ rootState, commit }, explanatoryData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/add-explanatory&id=${explanatoryData.violationId}`, token);
        const formData = new FormData();

        formData.append("text", explanatoryData.explanatoryContent);
        explanatoryData.files.forEach((file) => {
            formData.append("files[]", file);
        });

        const { data } = await axios.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        if (data) {
            commit("setExplanatoryToFineOrBonus", { explanatory: data, violationId: explanatoryData.violationId });
        }

        return data;
    },
    async updateViewOfExplanatory({ rootState, commit }, fineOrBonusId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/user-bonus-penalty/update&id=${fineOrBonusId}`, token);
        const formData = new FormData();

        formData.append("explanatory_viewed", 1);
        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("setExplanatoryViewed", fineOrBonusId);
        }

        return data;
    },
};

const mutations = {
    setFilialsCollection(state, filials) {
        state.collections.filials = filials;
    },
    setPositionsCollection(state, positions) {
        state.collections.positions = positions;
    },
    setWorkersCollection(state, workers) {
        state.collections.workers = workers;
    },
    setRecords(state, records) {
        state.records = [...state.records, ...records];
    },
    setFirstRecords(state, records) {
        state.records = records;
    },
    setColumns(state, columns) {
        state.columns = columns;
    },
    setUserColumns(state, columns) {
        state.userColumns = columns;
    },
    putNewFineOrBonus(state, fineOrBonus) {
        state.records.unshift(fineOrBonus);
    },
    updateFineOrBonus(state, updatedFineOrBonus) {
        const fineOrBonusIndex = state.records.findIndex((r) => r.id === updatedFineOrBonus.id);
        state.records.splice(fineOrBonusIndex, 1, updatedFineOrBonus);
    },
    deleteFineOrBonus(state, fineOrBonusId) {
        const recordIndex = state.records.findIndex((r) => r.id === fineOrBonusId);
        state.records.splice(recordIndex, 1);
    },
    setIsMoreDataAvailable(state, value) {
        state.isMoreDataAvailable = value;
    },
    setTotalCount(state, value) {
        state.totalCount = value;
    },
    setExplanatoryViewed(state, fineOrBonusId) {
        const fineOrBonus = state.records.find((record) => record.id === fineOrBonusId);
        fineOrBonus.explanatory.is_viewed = 1;
    },
    setFinesAndBonuses(state, items) {
        state.collections.finesAndBonuses = items;
    },
    setExplanatoryToFineOrBonus(state, { violationId, explanatory }) {
        const violation = state.records.find((record) => record.id === violationId);
        violation.explanatory = { ...explanatory, is_viewed: 0 };
    },
    setQueryForGoogleSheets(state, query) {
        state.queryForGoogleSheets = query;
    },
    setCurrentUserWorker(state, currentUserWorker) {
        state.currentUserWorker = currentUserWorker;
    },
    setCompanies(state, companies) {
        state.collections.companies = companies;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
